import { useState, useMemo, useEffect } from 'react';
import { SectionLabel } from '../components/primitives.js';
import {
  canopusCommandService,
  type CanopusLeaderboardEntry,
  type CanopusPerformanceLeaderboard,
  type CanopusSIPeriod,
} from '../../../services/canopusCommand.js';
// synthetic per-entry "Avg Team TFI" value for the leaderboard.
// SI overlay — replaces zero siTotal/siOffCase/siPerCase from
// the server with deterministic plausible numbers while preserving real names.
// specialty rename + merge across leaderboard buckets.
import {
  applyDemoSIToLeaderboard,
  applyDemoSpecialtyNormalization,
  getDemoPerformanceLeaderboard,
  getDemoTfiForLeaderboardEntry,
} from '../data/demo-data.js';
// Forces a capital first letter on staff names that come in lowercase.
import { capitalizeName } from './staff.js';
import './performance.css';

type LeaderboardCategory = 'surgeons' | 'staff' | 'rooms' | 'specialties' | 'procedures';

type MetricId =
  | 'cases'
  | 'avgVariance'
  | 'avgDur'
  | 'consistency'
  | 'improvement'
  | 'utilization'
  | 'siTotal'
  | 'siOffCase'
  | 'siPerCase'
  // demo-only metric, not part of the real backend payload.
  | 'tfiAvg';

type LeaderboardMetricKey = Exclude<
  keyof CanopusLeaderboardEntry,
  'name' | 'specialty' | 'position' | 'bestDay' | 'trend'
>;

// widen the key type with 'tfiAvg' — the real
// CanopusLeaderboardEntry doesn't have that field; it's synthesized on
// the client when demoMode is active.
type MetricDefKey = LeaderboardMetricKey | 'tfiAvg';

interface MetricDef {
  id: MetricId;
  label: string;
  description: string;
  key: MetricDefKey;
  higher: boolean;
  unit: string;
  fmt: (v: number | null | undefined) => string | number;
}

// ─── Sparkline ─ mini trend line ──────────────────────────────────
function Sparkline({
  data,
  color,
  w = 80,
  h = 24,
}: {
  data: number[];
  color: string;
  w?: number;
  h?: number;
}) {
  if (!data || data.length < 2) return <span className="perf-spark-empty">--</span>;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const points = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - ((v - min) / range) * (h - 4) - 2;
      return `${x},${y}`;
    })
    .join(' ');
  const lastVal = data[data.length - 1] ?? 0;
  return (
    // svg width/height/coords are intrinsic geometry, not styling.
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={((data.length - 1) / (data.length - 1)) * w}
        cy={h - ((lastVal - min) / range) * (h - 4) - 2}
        r="2.5"
        fill={color}
      />
    </svg>
  );
}

// ─── MedalIcon ─ rank medal for top 3 ────────────────────────────
function MedalIcon({ rank }: { rank: number }) {
  const colors: Record<number, string> = { 1: '#FFD700', 2: '#C0C0C0', 3: '#CD7F32' };
  if (rank > 3) return <span className="perf-medal-rank">{rank}</span>;
  // Medal background/border driven by per-rank metallic colors — kept inline.
  return (
    <div
      className="perf-medal"
      style={{
        background: `${colors[rank]}22`,
        border: `2px solid ${colors[rank]}`,
      }}
    >
      <span className="perf-medal-num" style={{ color: colors[rank] }}>
        {rank}
      </span>
    </div>
  );
}

// ─── PerformanceTab ───────────────────────────────────────────────
export function PerformanceTab({
  facilityId,
  /** UK facilities use "Operating Theatres"; everywhere else "Operating Rooms". */
  roomFullLabelPlural = 'Operating Rooms',
  // gates the synthetic "Avg Team TFI" leaderboard metric.
  demoMode = false,
}: {
  facilityId: string | null;
  roomFullLabelPlural?: string;
  demoMode?: boolean;
}) {
  const [category, setCategory] = useState<LeaderboardCategory>('surgeons');
  const [metric, setMetric] = useState<MetricId>('cases');
  const [period, setPeriod] = useState<CanopusSIPeriod>('all');
  const [selectedEntity, setSelectedEntity] = useState<CanopusLeaderboardEntry | null>(null);
  const [leaderboard, setLeaderboard] = useState<CanopusPerformanceLeaderboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!facilityId) return;
    // Demo facility: no backend to query. Synthesize a base leaderboard from
    // the baked demo pools, then run it through the same overlays the live
    // path uses. Never call the service (the fake id would 404).
    if (demoMode) {
      const base = getDemoPerformanceLeaderboard(period);
      setLeaderboard(applyDemoSpecialtyNormalization(applyDemoSIToLeaderboard(base)));
      setSelectedEntity(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    canopusCommandService
      .getPerformanceLeaderboard(facilityId, period)
      .then((data) => {
        if (cancelled) return;
        setLeaderboard(data);
        setSelectedEntity(null);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load performance data');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [facilityId, period, demoMode]);

  useEffect(() => {
    if (category !== 'staff' && metric === 'siOffCase') {
      setMetric('cases');
    }
  }, [category, metric]);

  const categories: Array<{ id: LeaderboardCategory; label: string }> = [
    { id: 'surgeons', label: 'Surgeons' },
    { id: 'staff', label: 'Staff' },
    { id: 'rooms', label: roomFullLabelPlural },
    { id: 'specialties', label: 'Specialties' },
    { id: 'procedures', label: 'Procedures' },
  ];

  const periods: Array<{ id: CanopusSIPeriod; label: string; capsLabel: string }> = [
    { id: '30d', label: '30 Days', capsLabel: 'OVER 30 DAYS' },
    { id: '90d', label: '90 Days', capsLabel: 'OVER 90 DAYS' },
    { id: 'ytd', label: 'YTD', capsLabel: 'YEAR TO DATE' },
    { id: 'all', label: 'All Time', capsLabel: 'OVER ALL TIME' },
  ];

  const metrics: MetricDef[] = useMemo(() => {
    const base: MetricDef[] = [
      {
        id: 'cases',
        label: 'Case Volume',
        description: 'Total number of surgical cases performed in the selected period.',
        key: 'cases',
        higher: true,
        unit: '',
        fmt: (v) => (v != null ? v : '--'),
      },
      {
        id: 'avgVariance',
        label: 'Avg Variance',
        description:
          'Average difference between scheduled and actual case duration (minutes). Lower is better.',
        key: 'avgVariance',
        higher: false,
        unit: 'm',
        fmt: (v) => (v != null ? `${v}m` : '--'),
      },
      {
        id: 'avgDur',
        label: 'Avg Duration',
        description: 'Average actual case duration across all cases in the selected period.',
        key: 'avgDur',
        higher: false,
        unit: 'm',
        fmt: (v) =>
          v != null
            ? `${Math.floor(v / 60)}h ${Math.round(v % 60)
                .toString()
                .padStart(2, '0')}m`
            : '--',
      },
      {
        id: 'consistency',
        label: 'Consistency',
        description:
          'How reliably cases finish within their scheduled time window. Higher is better.',
        key: 'consistency',
        higher: true,
        unit: '%',
        fmt: (v) => (v != null ? `${v}%` : '--'),
      },
      {
        id: 'improvement',
        label: 'Improvement Rate',
        description:
          'Rate of improvement in scheduling accuracy over the selected period. Higher is better.',
        key: 'improvement',
        higher: true,
        unit: '%',
        fmt: (v) => (v != null ? `${v}%` : '--'),
      },

      {
        id: 'siTotal',
        label: 'Total SIs',
        description: 'Total number of surgical inquiries logged in the selected period.',
        key: 'siTotal',
        higher: true,
        unit: '',
        fmt: (v) => (v != null ? v.toLocaleString() : '--'),
      },
      {
        id: 'siOffCase',
        label: 'Off-Case SIs',
        description: 'Surgical inquiries submitted outside of an active case.',
        key: 'siOffCase',
        higher: true,
        unit: '',
        fmt: (v) => (v != null ? v.toLocaleString() : '--'),
      },
      {
        id: 'siPerCase',
        label: 'SI Density',
        description: 'Average number of surgical inquiries per case.',
        key: 'siPerCase',
        higher: true,
        unit: '/case',
        fmt: (v) => (v != null ? `${v}/case` : '--'),
      },
    ];
    // always include the "Avg Team TFI" metric pill. In
    // demo mode each entry gets a deterministic synthesized value; in live
    // mode every entry returns null and the pill / rows show "--" since the
    // server doesn't compute TFI yet.
    base.push({
      id: 'tfiAvg',
      label: 'Avg Team TFI',
      description: 'Average Team Task Force Index score. Higher is better.',
      key: 'tfiAvg',
      higher: true,
      unit: '',
      fmt: (v) => (v != null ? Math.round(v).toString() : '--'),
    });
    return base;
  }, [demoMode]);

  const currentMetric: MetricDef =
    metrics.find((m) => m.id === metric) ?? (metrics[0] as MetricDef);
  const data: CanopusLeaderboardEntry[] = leaderboard ? (leaderboard[category] ?? []) : [];

  // Staff names occasionally come in fully lowercase from the source data;
  // force a capital first letter for the staff bucket only. Rooms/specialties/
  // procedures already use the right casing (e.g. "OR 3", "ALIF L4-L5") and
  // would be damaged by blanket capitalization.
  const displayName = (name: string): string =>
    category === 'staff' ? (capitalizeName(name) ?? name) : name;

  const getMetricValue = (entry: CanopusLeaderboardEntry, key: MetricDefKey): number | null => {
    // synthesize a deterministic per-entry TFI value in
    // demo mode. Outside demo mode the metric isn't even present in the
    // pill list (see `metrics` above), but guard anyway so the function
    // returns null if someone references it.
    if (key === 'tfiAvg') {
      return demoMode ? getDemoTfiForLeaderboardEntry(entry.name) : null;
    }
    const v = entry[key as LeaderboardMetricKey];
    return typeof v === 'number' ? v : null;
  };

  const sorted = useMemo(() => {
    const filtered = data.filter((d) => getMetricValue(d, currentMetric.key) !== null);
    const nullItems = data.filter((d) => getMetricValue(d, currentMetric.key) === null);
    const s = [...filtered].sort((a, b) => {
      const av = getMetricValue(a, currentMetric.key) ?? 0;
      const bv = getMetricValue(b, currentMetric.key) ?? 0;
      return currentMetric.higher ? bv - av : av - bv;
    });
    return [...s, ...nullItems];
  }, [data, currentMetric]);

  const topEntry = sorted[0];
  const topVal = topEntry ? getMetricValue(topEntry, currentMetric.key) : null;
  const maxVal = topVal != null ? topVal : 1;
  return (
    <div>
      {/* Period picker */}
      <div className="perf-period-row">
        {periods.map((p) => (
          <button
            key={p.id}
            onClick={() => setPeriod(p.id as CanopusSIPeriod)}
            className={`perf-period-btn ${period === p.id ? 'perf-period-btn--active' : ''}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Category tabs */}
      <div className="perf-cat-tabs">
        {categories.map((c) => (
          <button
            key={c.id}
            onClick={() => {
              setCategory(c.id);
              setSelectedEntity(null);
            }}
            className={`perf-cat-btn ${category === c.id ? 'perf-cat-btn--active' : ''}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Metric pills */}
      <div className="perf-metric-row">
        {metrics
          .filter((m) => m.id !== 'siOffCase' || category === 'staff')
          .map((m) => (
            <button
              key={m.id}
              onClick={() => setMetric(m.id)}
              className={`perf-metric-pill metric-tip ${metric === m.id ? 'perf-metric-pill--active' : ''}`}
            >
              {m.label}
              <span className="metric-tip-text">{m.description}</span>
            </button>
          ))}
      </div>

      <div className={`perf-grid ${selectedEntity ? 'perf-grid--split' : 'perf-grid--single'}`}>
        {/* Leaderboard list */}
        <div className="perf-board">
          <div className="perf-board-header">
            <div className="perf-board-header-left">
              <SectionLabel
                text={`${categories.find((c) => c.id === category)?.label ?? ''} — Ranked by ${currentMetric.label}`}
              />
              <span className="perf-period-caps-label metric-tip">
                {periods.find((p) => p.id === period)?.capsLabel ?? ''}
                <span className="metric-tip-text">
                  <div style={{ marginBottom: '6px' }}>
                    <strong>{currentMetric.label}:</strong> {currentMetric.description}
                  </div>
                  <div>
                    Rankings calculated{' '}
                    {periods.find((p) => p.id === period)?.capsLabel?.toLowerCase() ?? ''}.
                    Duration-based metrics are derived from reference guide check-off data.
                    Consistency and Improvement Rate require a minimum of 15 cases.
                  </div>
                </span>
              </span>
            </div>
            <span className="perf-board-hint">
              {currentMetric.higher ? 'Higher is better' : 'Lower is better'}
            </span>
          </div>

          {loading && (
            <div>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="perf-skel-row">
                  <div className="perf-skel-rank loading-blur">
                    <span className="perf-skel-rank-text">{i}</span>
                  </div>
                  <div className="perf-skel-body">
                    <div className="perf-skel-line">
                      <span className="loading-blur perf-skel-name">Surgeon Name</span>
                      <span className="loading-blur perf-skel-tag">Specialty</span>
                    </div>
                    <div className="perf-skel-bar-wrap">
                      <div className="loading-blur perf-skel-bar-fill" />
                    </div>
                  </div>
                  <div className="perf-skel-val-wrap">
                    <div className="loading-blur perf-skel-val">00%</div>
                    <div className="loading-blur perf-skel-val-sub">0 cases</div>
                  </div>
                  <div className="perf-skel-spark">
                    <div className="loading-blur perf-skel-spark-bg" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && error && <div className="perf-empty perf-empty--err">{error}</div>}
          {!loading && !error && sorted.length === 0 && (
            <div className="perf-empty perf-empty--muted">
              {facilityId ? 'No data available for this period' : 'No facility selected'}
            </div>
          )}

          {!loading &&
            sorted.map((entry, i) => {
              const rank = i + 1;
              const val = getMetricValue(entry, currentMetric.key);
              const barPct =
                val != null
                  ? currentMetric.higher
                    ? (val / maxVal) * 100
                    : (1 - val / (maxVal * 1.5)) * 100 + 30
                  : 0;
              const isSel = selectedEntity && selectedEntity.name === entry.name;
              const isTop3 = rank <= 3 && val != null;
              const rowClass = `perf-row ${isSel ? 'perf-row--sel' : isTop3 ? 'perf-row--top3' : ''}`;
              // Bar fill encodes rank (gradient) + numeric pct width — kept inline.
              const barBg =
                isTop3 && rank === 1
                  ? 'linear-gradient(90deg, #FFD700, #FFA500)'
                  : isTop3 && rank === 2
                    ? 'linear-gradient(90deg, #C0C0C0, #A0A0A0)'
                    : isTop3 && rank === 3
                      ? 'linear-gradient(90deg, #CD7F32, #A0652A)'
                      : 'rgba(15,183,209,0.4)';
              return (
                <div
                  key={i}
                  onClick={() => setSelectedEntity(isSel ? null : entry)}
                  className={rowClass}
                  onMouseOver={(e) => {
                    if (!isSel) e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                  }}
                  onMouseOut={(e) => {
                    if (!isSel && !isTop3) e.currentTarget.style.background = 'transparent';
                    else if (!isSel) e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                  }}
                >
                  <MedalIcon rank={rank} />
                  <div className="perf-row-body">
                    <div className="perf-row-line">
                      <span className={`perf-row-name ${isTop3 ? 'perf-row-name--top3' : ''}`}>
                        {displayName(entry.name)}
                      </span>
                      {entry.specialty && <span className="perf-row-tag">{entry.specialty}</span>}
                      {entry.position && <span className="perf-row-tag">{entry.position}</span>}
                    </div>
                    <div className="perf-row-bar-wrap">
                      <div
                        className="perf-row-bar-fill"
                        style={{
                          width: `${Math.max(barPct, 5)}%`,
                          background: barBg,
                        }}
                      />
                    </div>
                  </div>
                  <div className="perf-row-val-wrap">
                    <div
                      className={`perf-row-val ${
                        val == null
                          ? 'perf-row-val--null'
                          : isTop3
                            ? 'perf-row-val--top3'
                            : 'perf-row-val--present'
                      }`}
                    >
                      {currentMetric.fmt(val)}
                    </div>
                    <div className="perf-row-cases">{entry.cases} cases</div>
                  </div>
                  <div className="perf-row-spark">
                    <Sparkline
                      data={entry.trend.filter((v): v is number => v != null)}
                      color={isTop3 ? '#0FB7D1' : '#667788'}
                    />
                  </div>
                </div>
              );
            })}
        </div>

        {/* Detail panel */}
        {selectedEntity && (
          <div>
            <div className="perf-detail-card">
              <div className="perf-detail-header">
                <div>
                  <div className="perf-detail-name">{displayName(selectedEntity.name)}</div>
                  <div className="perf-detail-meta">
                    {selectedEntity.specialty || selectedEntity.position || ''}
                  </div>
                </div>
                <button onClick={() => setSelectedEntity(null)} className="perf-detail-close">
                  Close
                </button>
              </div>
              <div className="perf-grid--three">
                {metrics.map((m) => {
                  const v = getMetricValue(selectedEntity, m.key);
                  const tileClass = `perf-metric-tile metric-tip ${metric === m.id ? 'perf-metric-tile--active' : ''}`;
                  const valClass = `perf-metric-value ${metric === m.id ? 'perf-metric-value--active' : ''}`;
                  return (
                    <div key={m.id} className={tileClass}>
                      <div className="perf-metric-label">{m.label}</div>
                      <div className={valClass}>{m.fmt(v)}</div>
                      <span className="metric-tip-text">{m.description}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {selectedEntity.trend && selectedEntity.trend.some((v) => v != null) && (
              <div className="perf-card">
                <SectionLabel text="Duration Variance Trend (6-Month)" />
                <div className="perf-trend-row">
                  {selectedEntity.trend.map((v, i) => {
                    const nonNullVals = selectedEntity.trend.filter((x): x is number => x != null);
                    const max = nonNullVals.length > 0 ? Math.max(...nonNullVals) : 0;
                    const pct = v != null && max > 0 ? v / max : 0;
                    const isLast = i === selectedEntity.trend.length - 1;
                    // height encodes numeric pct of max — kept inline.
                    return (
                      <div key={i} className="perf-trend-col">
                        <span className={`perf-trend-val ${isLast ? 'perf-trend-val--last' : ''}`}>
                          {v != null ? `${v}m` : ''}
                        </span>
                        <div
                          className={`perf-trend-bar ${isLast ? 'perf-trend-bar--last' : ''}`}
                          style={{
                            height: v != null ? Math.max(80 * pct, 6) : 0,
                            minHeight: v != null ? 6 : 0,
                          }}
                        />
                        <span className="perf-trend-month">
                          {leaderboard?.trendMonths?.[i] ?? ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
